import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Container, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FooterText } from "../components/footer/FooterText";

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(8, 0, 2),
  },
  section: {
    marginTop: theme.spacing(3),
  },
  list: {
    margin: 0,
    paddingLeft: theme.spacing(3),
  },
  homeLink: {
    color: theme.palette.foreground.default,
    textDecoration: "none",
    transition: "color 0.3s ease",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
}));

export const PrivacyPolicyPage = () => {
  const classes = useStyles();

  return (
    <Box className={classes.page}>
      <Container maxWidth="md">
        <Typography variant="body2" paragraph>
          <RouterLink to="/" className={classes.homeLink}>
            Back to Home
          </RouterLink>
        </Typography>
        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Last updated: 4 June 2026
        </Typography>

        <Box className={classes.section}>
          <Typography variant="body1" paragraph>
            This Privacy Policy applies to the Hulmify landing website and all
            Hulmify applications, products, and services that link to this
            policy (collectively, the "Services"). It explains how we collect,
            use, share, retain, and protect personal information when people
            browse, sign up, or use any Hulmify app.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            1. Information We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on how you interact with the Services, we may collect:
          </Typography>
          <ul className={classes.list}>
            <li>
              <Typography variant="body1">
                Identity and contact data (name, email, company, role).
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Account and profile data (credentials, preferences).
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Usage and device data (IP address, browser, pages viewed,
                events, diagnostics).
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Communications data (support requests, feedback, survey
                responses).
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Transactional data where applicable (plans, subscriptions,
                invoices).
              </Typography>
            </li>
          </ul>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            2. How We Use Information
          </Typography>
          <ul className={classes.list}>
            <li>
              <Typography variant="body1">
                Operate, maintain, and improve Hulmify apps and infrastructure.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Authenticate users and secure accounts.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Provide support and service communications.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Analyze performance and product usage to improve features.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Meet legal, regulatory, tax, accounting, and compliance
                obligations.
              </Typography>
            </li>
          </ul>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            3. Legal Bases (GDPR / UK GDPR)
          </Typography>
          <Typography variant="body1" paragraph>
            Where required, we process personal data under one or more lawful
            bases: consent, contract performance, legitimate interests, legal
            obligations, and protection against fraud, abuse, and security
            incidents.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            4. Cookies and Similar Technologies
          </Typography>
          <Typography variant="body1" paragraph>
            We use cookies and similar tools for essential site functions,
            analytics, performance, and user experience optimization. Where
            required by law, we request consent before setting non-essential
            cookies. Browser controls can also be used to manage cookie
            preferences.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            5. Sharing and Disclosure
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell personal information. We may share data with service
            providers (hosting, analytics, communication, payment processors),
            affiliates, and legal authorities where needed for service delivery,
            compliance, security, or lawful requests.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            6. International Data Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            Data may be transferred and processed outside your country. Where
            required, we use appropriate safeguards such as contractual
            protections and comparable legal mechanisms.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            7. Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain personal information only as long as reasonably necessary
            for business purposes, contractual commitments, legal obligations,
            and dispute resolution.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            8. Security
          </Typography>
          <Typography variant="body1" paragraph>
            We maintain administrative, technical, and organizational safeguards
            designed to protect personal information. No internet transmission
            or storage method is fully secure, so absolute security cannot be
            guaranteed.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            9. Regional Privacy Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on your location, you may have rights to access, correct,
            delete, port, object to, restrict, or withdraw consent for certain
            processing.
          </Typography>
          <ul className={classes.list}>
            <li>
              <Typography variant="body1">
                EEA/UK/Switzerland: rights under GDPR and UK GDPR.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                California: rights under CCPA/CPRA, including rights to know,
                delete, and correct.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Canada: protections under PIPEDA and applicable provincial laws.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Brazil: rights under LGPD.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Other jurisdictions: rights under applicable local privacy
                legislation.
              </Typography>
            </li>
          </ul>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            10. Children
          </Typography>
          <Typography variant="body1" paragraph>
            The Services are not directed to children under the age required by
            applicable law. If you believe a child has provided personal
            information, contact us so we can take appropriate action.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            11. Compliance Frameworks Covered
          </Typography>
          <Typography variant="body1" paragraph>
            This policy is intended to support compliance with major privacy
            frameworks, including GDPR, UK GDPR, CCPA/CPRA, CalOPPA, PIPEDA, and
            LGPD, alongside other applicable laws.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            12. Changes to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this policy periodically. Material updates will be
            posted on this page with a revised "Last updated" date.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            13. Contact
          </Typography>
          <Typography variant="body1" paragraph>
            For privacy requests or questions across any Hulmify app, use the
            contact channels listed on this website and include "Privacy
            Request" in your message.
          </Typography>
        </Box>
      </Container>
      <FooterText />
    </Box>
  );
};
